import WebSocket from 'ws';
import { buildCliWebSocketClientOptions, safeCloseWebSocket } from '../connection/cli-ws-client.js';
import { buildFirehoseCliWsUrl } from './build-cli-ws-url.js';
import { createFirehoseMessageDeps } from './create-firehose-message-deps.js';
import { wireFirehoseWebSocket } from './wire-firehose-websocket.js';
import type {
  ConnectFirehoseOptions,
  FirehoseConnectionHandle,
} from './firehose-connection-types.js';

export type {
  ConnectFirehoseOptions,
  FirehoseConnectionHandle,
  FirehoseIdentifyParams,
} from './firehose-connection-types.js';

/**
 * Separate WebSocket to the firehose service only for (1) preview HTTP proxy to localhost and
 * (2) preview-environment live logs. File browser and other bridge RPC traffic stay on the main bridge
 * WebSocket to the API/Bridge DO.
 *
 * `proxy` control messages and trailing binary body frames are handled synchronously so metadata
 * is registered before the body frame and many preview requests can start in parallel without
 * waiting behind deferred log traffic. Other JSON (log viewers, identified) is deferred one tick.
 * Heavy work (local fetch / streaming) stays async inside handlers.
 */
export function connectFirehose(options: ConnectFirehoseOptions): FirehoseConnectionHandle {
  const {
    firehoseServerUrl,
    workspaceId,
    bridgeName,
    proxyPorts,
    log,
    previewEnvironmentManager,
    onOpen,
    onClose,
    suppressWebSocketErrors,
  } = options;

  const wsUrl = buildFirehoseCliWsUrl(firehoseServerUrl);
  const ws = new WebSocket(wsUrl, buildCliWebSocketClientOptions(wsUrl));

  const deps = createFirehoseMessageDeps(ws, log, previewEnvironmentManager);
  const wired = wireFirehoseWebSocket({
    ws,
    deps,
    log,
    previewEnvironmentManager,
    workspaceId,
    bridgeName,
    proxyPorts,
    onOpen,
    onClose,
    suppressWebSocketErrors,
  });

  return {
    close() {
      wired.disposeClientPing();
      wired.detachPreviewEnvironmentManager();
      safeCloseWebSocket(ws);
    },
    isConnected: () => ws.readyState === WebSocket.OPEN,
    updateIdentify: wired.sendIdentify,
  };
}
